import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Avatar, Box, Button, Grid, Typography, Container, Link } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm, FieldErrors, DefaultValues } from "react-hook-form";
import * as yup from "yup";

import ContrastIcon from "../ContrastIcon";
import CircularLoader from "../CircularLoader";

interface LinkType {
  label: string;
  href: string;
}

export interface AuthFormProps<T> {
  avatarIcon: string;
  children: React.ReactNode;
  links?: Array<LinkType>;
  notInputChildren?: Array<number>;
  onSubmit: (values: T) => Promise<void>;
  submitDisabled?: boolean;
  submitLabel: string;
  title: string;
  validationSchema: yup.ObjectSchema<any>;
}

function AuthForm<T extends Record<string, unknown>>(props: AuthFormProps<T>) {
  const {
    avatarIcon,
    children,
    links,
    // Mark by index which children is not an input field so input props
    // won't be added to it. Otherwise errors will be thrown in console
    notInputChildren,
    onSubmit: onSubmitProp,
    submitDisabled = false,
    submitLabel,
    title,
    validationSchema,
  } = props;
  const [loading, setLoading] = useState(false);

  const setInitialValues = (elements: React.ReactNode) => {
    const initialValues: Record<string, unknown> = {};
    if (React.Children.count(elements) === 1) {
      initialValues[(elements as React.ReactElement).props.name] = "";
    } else {
      (elements as React.ReactElement[]).forEach((child) => {
        initialValues[child.props.name] = "";
      });
    }

    return initialValues as DefaultValues<T>;
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<T>({
    defaultValues: setInitialValues(children),
    resolver: yupResolver(validationSchema),
  });

  const getController = (element: JSX.Element, elementErrors: FieldErrors) => (
    <Controller
      control={control}
      name={element.props.name}
      render={({ field }) =>
        React.cloneElement(element, {
          ...field,
          error: Boolean(elementErrors[element.props.name]),
          helperText: elementErrors[element.props.name]?.message,
        })
      }
    />
  );

  const onSubmit = async (values: T) => {
    const trimmedValues: Record<string, unknown> = {};
    // Remove leading and trailing spaces from input values
    Object.entries(values).forEach((keyValue: [string, unknown]) => {
      const [key, value] = keyValue;
      // Remove leading and trailing only if it's not the password field
      if (key !== "password" && typeof value === "string") {
        trimmedValues[key] = value.replace(/ +(?= )/g, "").trim();
      } else {
        trimmedValues[key] = value;
      }
    });

    setLoading(true);
    await onSubmitProp(trimmedValues as T);
    setLoading(false);
  };

  const renderSingleChild = (child: React.ReactNode) => {
    if (!React.isValidElement(child)) return null;
    const clonedElement = getController(child, errors);

    return React.Children.only(clonedElement);
  };

  return (
    <Container maxWidth='xs'>
      <Box display='flex' flexDirection='column' alignItems='center' my={8}>
        {/* HEADER */}
        <Avatar sx={{ bgcolor: "primary.main", margin: 1 }}>
          <ContrastIcon path={avatarIcon} size={1} />
        </Avatar>
        <Typography role='heading' variant='h5'>
          {title}
        </Typography>

        {/* FORM */}
        <form style={{ marginTop: "1.5rem", width: "100%" }} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={React.Children.count(children) > 1 ? 2 : 0}>
            {React.Children.count(children) > 1
              ? (children as React.ReactElement[]).map((child, index) => {
                  const clonedElement = notInputChildren?.includes(index) ? child : getController(child, errors);

                  return (
                    <Grid item xs={12} key={index} display='flex' justifyContent='center'>
                      {clonedElement}
                    </Grid>
                  );
                })
              : renderSingleChild(children)}
          </Grid>

          {/* SUBMIT BUTTON */}
          <CircularLoader loading={loading}>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              fullWidth
              disabled={loading || submitDisabled}
              sx={{ mt: 3, mb: 2 }}
            >
              {submitLabel}
            </Button>
          </CircularLoader>
        </form>

        {/* LINKS */}
        {links?.length ? (
          <Grid container justifyContent={links.length > 1 ? "space-between" : "flex-end"}>
            {links.map((link) => (
              <Grid item key={link.label}>
                <Link component={RouterLink} to={link.href}>
                  {link.label}
                </Link>
              </Grid>
            ))}
          </Grid>
        ) : null}
      </Box>
    </Container>
  );
}

export default AuthForm;
